import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import googlePlay from '../../assets/img/logo/google-play-app-download-logo.png';
import appleStore from '../../assets/img/logo/apple-store-app-download-logo.png';
import FixedPlugin from '../../components/fixedPlugin/FixedPlugin';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
	MdTrendingUp,
	MdSpeed,
	MdDone,
	MdBarChart,
	MdLightbulb,
	MdContactSupport
} from 'react-icons/md';
import { FaLinkedin, FaTwitter, FaFacebook } from 'react-icons/fa';

export default function Homepage() {
	const { isAuthenticated, userProfile } = useAuth();

	return (
		<div className="min-h-screen dark:bg-gray-900">
			<FixedPlugin />
			<Header />

			{/* Hero Section */}
			<section className="py-20 px-4">
				<div className="max-w-4xl mx-auto text-center">
					<h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-6">
						Smarter, Faster, Interim Valuation for Construction Projects
					</h1>
					<p className="text-lg text-gray-700 dark:text-gray-300 mb-12">
						Powered by Extended Reality and GenAI to automate your interim reports
					</p>
				</div>
			</section>

			{/* Features Section */}
			<section className="py-16 px-4 bg-gray-50 dark:bg-gray-900">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">How It Works</h2>
						<p className="text-lg text-gray-700 dark:text-gray-300 mt-4">
							Everything you need to automate your construction valuation process
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{/* Feature Card 1 */}
						<div className="shadow-sm hover:shadow-md transition-shadow duration-200 bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 p-8 rounded-lg">
							<div className="text-4xl mb-4">📱</div>
							<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Download the app</h3>
							<p className="text-gray-700 dark:text-gray-300 mb-4">
								Get our mobile application to start capturing construction progress with AR technology and precision measurements.
							</p>
						</div>

						{/* Feature Card 2 */}
						<div className="shadow-sm hover:shadow-md transition-shadow duration-200 bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 p-8 rounded-lg">
							<div className="text-4xl mb-4">📄</div>
							<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Upload Bill of Quantities</h3>
							<p className="text-gray-700 dark:text-gray-300 mb-4">
								Upload your BOQ documents and let our AI process them automatically for instant analysis and report generation.
							</p>
							{isAuthenticated && userProfile?.userApprovalStatus === 'Approved' && (
								<Link
									to="/uploadboq"
									className="inline-block text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
								// className="bg-blue-600 hover:bg-blue-700/65 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200 flex items-center gap-2"
								>
									Upload Now →
								</Link>
							)}
						</div>

						{/* Feature Card 3 */}
						<div className="shadow-sm hover:shadow-md transition-shadow duration-200 bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 p-8 rounded-lg">
							<div className="text-4xl mb-4">📐</div>
							<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Measure on site</h3>
							<p className="text-gray-700 dark:text-gray-300 mb-4">
								Use AR technology to capture accurate measurements and progress data directly from your construction site.
							</p>
						</div>

						{/* Feature Card 4 */}
						<div className="shadow-sm hover:shadow-md transition-shadow duration-200 bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 p-8 rounded-lg">
							<div className="text-4xl mb-4">📋</div>
							<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Generate interim valuation</h3>
							<p className="text-gray-700 dark:text-gray-300 mb-4">
								Automatically generate comprehensive interim valuation reports with real-time data and professional formatting.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* App Download & Social Media Section */}
			<section className="py-12 px-4 bg-white dark:bg-gray-900 rounded-xl">
				<div className="max-w-6xl py-24 mx-auto shadow-sm bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 p-8 rounded-lg">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-2 pb-10 items-start">
						{/* App Download Section */}
						<div className="text-center">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								Download ValuePay XR App
							</h2>
							<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
								<img src={appleStore} alt="Download on the App Store" title="Download on the App Store" className="h-12 transition-opacity cursor-pointer bg-white/70 rounded-md hover:opacity-65" />
								<img src={googlePlay} alt="Get it on Google Play" title="Get it on Google Play" className="h-12 transition-opacity cursor-pointer bg-white/70 rounded-md hover:opacity-65" />
							</div>
						</div>

						{/* Social Media Section */}
						<div className="text-center">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 mt-8 lg:mt-0">
								Connect With Us
							</h2>
							<div className="flex items-center justify-center gap-4">
								<Link
									title='LinkedIn'
									className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 bg-blue-600/80 hover:bg-blue-700/65 dark:bg-blue-900/25 dark:hover:bg-gray-600/25"
								>
									<FaLinkedin className="w-6 h-6" />
								</Link>
								<Link
									title='Twitter'
									className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 bg-blue-600/80 hover:bg-blue-700/65 dark:bg-blue-900/25 dark:hover:bg-gray-600/25"
								>
									<FaTwitter className="w-6 h-6" />
								</Link>
								<Link
									title='Facebook'
									className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 bg-blue-600/80 hover:bg-blue-700/65 dark:bg-blue-900/25 dark:hover:bg-gray-600/25"
								>
									<FaFacebook className="w-6 h-6" />
								</Link>
							</div>
						</div>
					</div>

					{/* CTA Section */}
					<div className="px-4 ">
						<div className="max-w-4xl mx-auto text-center">
							{/* CTA Buttons */}
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								Start Chat with AI
							</h2>
							<div className="flex flex-col sm:flex-row items-center justify-center gap-4">

								{isAuthenticated && userProfile?.userApprovalStatus === 'Approved' && (
									<Link
										to="/chat"
										className="bg-blue-600 hover:bg-blue-700/65 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200 flex items-center gap-2"
										title='Start Chat'
									>
										💬 Start Chat
									</Link>
								)}
								{!isAuthenticated && (
									<Link
										to="/signin"
										className="bg-blue-600 hover:bg-blue-700/65 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200"
									>
										Get Started
									</Link>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Value Propositions Footer */}
			<section className="py-20 px-4">
				<div className="max-w-4xl mx-auto">
					<div className="text-center mb-8">
						<h3 className="text-xl font-semibold text-gray-900 dark:text-gray-300">Where Construction Meets Extended Reality and AI</h3>
					</div>

					<div className="grid grid-cols-3 md:grid-cols-6 gap-8 text-center">
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdTrendingUp className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Excellence</span>
						</div>
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdSpeed className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Efficiency</span>
						</div>
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdBarChart className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Effectiveness</span>
						</div>
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdDone className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Accuracy</span>
						</div>
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdLightbulb className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Innovation</span>
						</div>
						<div className="flex flex-col items-center">
							<div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-white dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25">
								<MdContactSupport className="w-8 h-8 text-gray-700 dark:text-gray-300" />
							</div>
							<span className="text-base font-medium text-gray-700 dark:text-gray-300">Reach out</span>
						</div>
					</div>
				</div>
			</section>

			<Footer />
		</div>
	);
}
